import { useState } from "react";
import { Helmet } from "react-helmet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, Search } from "lucide-react";
import { Link } from "wouter";
import { products } from "@/lib/data";

const Products = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  
  const categories = [
    { id: "all", name: "All Seeds" },
    { id: "major-seeds", name: "Major Seeds" },
    { id: "vegetable-seeds", name: "Vegetable Seeds" },
    { id: "upcoming-seeds", name: "Upcoming Seeds" }
  ];
  
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || product.category.toLowerCase() === activeCategory.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>Products - Varsha Seeds</title>
        <meta name="description" content="Explore our wide range of high-quality agricultural seeds for cereals, pulses, vegetables, and oil crops. Varsha Seeds - premium seeds for optimal yields." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary relative overflow-hidden py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YWdyaWN1bHR1cmUgZmllbGRzfHx8fHx8MTY5NzgzODc5OA&ixlib=rb-4.0.3&q=80&w=1920" 
            alt="Agricultural crop field" 
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6">
            Our Products
          </h1>
          <p className="text-white text-lg md:text-xl max-w-3xl mx-auto">
            Discover our premium range of high-quality seeds designed to maximize 
            yield, enhance crop resistance, and ensure sustainable agricultural success.
          </p>
        </div>
      </section>
      
      {/* Products Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search products..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex overflow-x-auto pb-2 md:pb-0">
              <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory}>
                <TabsList>
                  {categories.map((category) => (
                    <TabsTrigger key={category.id} value={category.id}>
                      {category.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
          
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <div key={index} className="bg-white border border-neutral-100 rounded-lg overflow-hidden shadow-md transition hover:shadow-xl">
                  <img 
                    src={product.imageUrl} 
                    alt={product.name} 
                    className="w-full h-56 object-cover"
                  />
                  <div className="p-6">
                    <span className="text-xs font-semibold text-primary bg-primary bg-opacity-10 px-3 py-1 rounded-full">
                      {product.category}
                    </span>
                    <h3 className="text-xl font-bold mt-3 mb-2 font-heading">{product.name}</h3>
                    <p className="text-neutral-700 mb-4">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <Link href={`/products/${product.slug}`}>
                        <a className="text-primary font-medium hover:text-primary-dark transition flex items-center gap-2">
                          View Details <ArrowRight className="h-4 w-4" />
                        </a>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium mb-2">No products found</h3>
              <p className="text-neutral-700 mb-6">Try adjusting your search or filter criteria.</p>
              <Button onClick={() => { setSearchTerm(""); setActiveCategory("all"); }}>
                Reset Filters
              </Button>
            </div>
          )}
        </div>
      </section>
      
      {/* Product Types Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-primary font-medium">Product Categories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Our Seed Collections
            </h2>
            <p className="text-neutral-700">
              We offer a wide range of seed varieties tailored to different 
              agricultural needs and growing conditions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1526394931762-8a4116f6e2c2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8d2hlYXQgZmllbGR8fHx8fHwxNjk3ODM4ODY1&ixlib=rb-4.0.3&q=80&w=500" 
                  alt="Major Seeds" 
                  className="w-full h-full object-cover transition-transform hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2 font-heading">Major Seeds</h3>
                <p className="text-neutral-700 mb-4">
                  Our flagship seed collection features premium varieties of wheat, maize, and bajra (pearl millet).
                  These high-yielding, disease-resistant seeds are specially bred for Indian agricultural conditions.
                </p>
                <Button 
                  onClick={() => { setActiveCategory("major-seeds"); }}
                  variant="outline" 
                  className="text-primary border-primary hover:bg-primary hover:text-white"
                >
                  Explore Major Seeds
                </Button>
              </div>
            </div>
            
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1563911892437-1feda0179e1b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8dmVnZXRhYmxlc3x8fHx8fDE2OTc4Mzg5MTU&ixlib=rb-4.0.3&q=80&w=500" 
                  alt="Vegetable Seeds" 
                  className="w-full h-full object-cover transition-transform hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2 font-heading">Vegetable Seeds</h3>
                <p className="text-neutral-700 mb-4">
                  Our vegetable seed collection offers premium varieties of tomatoes, onions, 
                  chilies, cabbage, cauliflower and more. Each seed is selected for 
                  superior taste, yield, and disease resistance.
                </p>
                <Button 
                  onClick={() => { setActiveCategory("vegetable-seeds"); }}
                  variant="outline" 
                  className="text-primary border-primary hover:bg-primary hover:text-white"
                >
                  Explore Vegetable Seeds
                </Button>
              </div>
            </div>
            
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1594650448918-33f855bb4929?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8Y290dG9uK3BsYW50fHx8fHx8MTY5NzgzODc5OA&ixlib=rb-4.0.3&q=80&w=500" 
                  alt="Upcoming Seeds" 
                  className="w-full h-full object-cover transition-transform hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2 font-heading">Upcoming Seeds</h3>
                <p className="text-neutral-700 mb-4">
                  Our newest seed innovations include high-protein soybean varieties and drought-resistant cotton. 
                  These seeds represent the future of sustainable and profitable farming.
                </p>
                <Button 
                  onClick={() => { setActiveCategory("upcoming-seeds"); }}
                  variant="outline" 
                  className="text-primary border-primary hover:bg-primary hover:text-white"
                >
                  Explore Upcoming Seeds
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-accent/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary font-heading">
                Need Help Selecting the Right Seeds?
              </h2>
              <p className="text-neutral-700 mb-6">
                Our agricultural experts are available to help you choose the best seed 
                varieties for your specific soil conditions, climate, and farming goals. 
                Get personalized recommendations for optimal yields.
              </p>
              <Link href="/contact">
                <Button className="bg-primary hover:bg-primary/90 text-white rounded-full">
                  Contact Our Experts
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1592982551635-069e7b9354da?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YWdyaWN1bHR1cmUgZmllbGRzfHx8fHx8MTY5NzgzODc5OA&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=600" 
                alt="Agricultural expert in field" 
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="h-5 w-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="font-semibold">98% satisfied customers</span>
                </div>
                <p className="text-neutral-700 text-sm">
                  "The expert advice from Varsha Seeds helped me increase my yield by 25%. 
                  Highly recommended!"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Products;
